import { SOUNDS } from '@constants/sounds'
import { useThrottledCallback } from '@hooks'
import { useBetSliderStore, useConfigurationStore, useMenuStore, usePlayerInfoStore, useSoundStore } from '@stores'
import { getBetValue } from '@utils/betOption'
import { AutoplayStopValueMessage, GameUIMessageScope, GameUIMessageType } from 'game-ui-types'
import { RefObject, useCallback, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from '@amusnet/translations/react'

const BALANCE_DECREASES_BY_MULTIPLIER = 0.8
const BALANCE_INCREASES_BY_MULTIPLIER = 2
const WIN_EXCEEDS_MULTIPLIER = 100

export interface IAutoplaySliderItem {
    title: string
    onChange: (currentValue: number) => void
    currentValue: number
    maxValue: number
    onSetEngineValue?: (value: number) => void
}

export const useAutoplaySliderItems: () => IAutoplaySliderItem[] = () => {
    const { t } = useTranslation()

    // Kept in the store so the values persist across menu open/close and are reset centrally
    // when autoplay completes/stops (see spinButtonSectionStore.updateAutoplayMode).
    const winExceedsMax = useMenuStore((state) => state.winExceedsMax)
    const winExceedsValue = useMenuStore((state) => state.winExceedsValue)
    const setWinExceedsValue = useMenuStore((state) => state.setWinExceedsValue)
    const setWinExceedsMax = useMenuStore((state) => state.setWinExceedsMax)
    const balanceDecreasesMax = useMenuStore((state) => state.balanceDecreasesMax)
    const setBalanceDecreasesMax = useMenuStore((state) => state.setBalanceDecreasesMax)
    const balanceDecreasesValue = useMenuStore((state) => state.balanceDecreasesValue)
    const setBalanceDecreasesValue = useMenuStore((state) => state.setBalanceDecreasesValue)
    const setBalanceIncreasesMax = useMenuStore((state) => state.setBalanceIncreasesMax)
    const balanceIncreasesMax = useMenuStore((state) => state.balanceIncreasesMax)
    const balanceIncreasesValue = useMenuStore((state) => state.balanceIncreasesValue)
    const setBalanceIncreasesValue = useMenuStore((state) => state.setBalanceIncreasesValue)

    const shouldBalanceSubtractOnly = useConfigurationStore((state) => state.shouldBalanceSubtractOnly)
    const balance = usePlayerInfoStore((state) => state.balance)
    const subtractedBalance = usePlayerInfoStore((state) => state.subtractedBalance)
    const activeIndex = useBetSliderStore((state) => state.activeIndex)
    const betOptions = useConfigurationStore((state) => state.betOptions)
    const displayedBalance = shouldBalanceSubtractOnly ? (subtractedBalance ?? balance) : balance

    const playEngineSound = useSoundStore((state) => state.playEngineSound)

    // We are going to use these refs for the autoplay click sounds
    const lastPlayedValueBalanceDecreases = useRef(0)
    const lastPlayedValueBalanceIncreases = useRef(0)
    const lastPlayedValueWinExceeds = useRef(0)

    useEffect(() => {
        setBalanceDecreasesMax(displayedBalance * BALANCE_DECREASES_BY_MULTIPLIER)
        setBalanceIncreasesMax(displayedBalance * BALANCE_INCREASES_BY_MULTIPLIER)
    }, [displayedBalance])

    useEffect(() => {
        setWinExceedsMax(getBetValue(betOptions[activeIndex]) * WIN_EXCEEDS_MULTIPLIER)
    }, [betOptions, activeIndex])

    const triggerSound = useThrottledCallback((value: number, lastPlayedValue: RefObject<number>, maxValue: number) => {
        const oldValuePercentage = (lastPlayedValue.current / maxValue) * 100
        const newValuePercentage = (value / maxValue) * 100

        if (Math.round(oldValuePercentage) !== Math.round(newValuePercentage)) {
            playEngineSound(SOUNDS.BET_SLIDE)
            lastPlayedValue.current = value
        }
    }, 50)

    const setEngineBalanceDecreasesValue = useCallback((value: number) => {
        const engineValueMessage: AutoplayStopValueMessage = {
            scope: GameUIMessageScope.MENU,
            type: GameUIMessageType.AUTOPLAY_SET_BALANCE_DECREASES,
            payload: value,
        }

        window.postMessage(engineValueMessage, '*')
    }, [])

    const setEngineBalanceIncreasesValue = useCallback((value: number) => {
        const engineValueMessage: AutoplayStopValueMessage = {
            scope: GameUIMessageScope.MENU,
            type: GameUIMessageType.AUTOPLAY_SET_BALANCE_INCREASES,
            payload: value,
        }

        window.postMessage(engineValueMessage, '*')
    }, [])

    const setEngineWinExceedsValue = useCallback((value: number) => {
        const engineValueMessage: AutoplayStopValueMessage = {
            scope: GameUIMessageScope.MENU,
            type: GameUIMessageType.AUTOPLAY_SET_WIN_EXCEEDS,
            payload: value,
        }

        window.postMessage(engineValueMessage, '*')
    }, [])

    const onBalanceDecreasesValueChange = (value: number) => {
        setBalanceDecreasesValue(value)
        triggerSound(value, lastPlayedValueBalanceDecreases, balanceDecreasesMax)
    }

    const onBalanceIncreasesValueChange = (value: number) => {
        setBalanceIncreasesValue(value)
        triggerSound(value, lastPlayedValueBalanceIncreases, balanceIncreasesMax)
    }

    const onWinExceedsValueChange = (value: number) => {
        setWinExceedsValue(value)
        triggerSound(value, lastPlayedValueWinExceeds, winExceedsMax)
    }

    const items = useMemo(
        () => [
            {
                title: t('gameui.balanceDecreases'),
                onChange: onBalanceDecreasesValueChange,
                onSetEngineValue: setEngineBalanceDecreasesValue,
                currentValue: balanceDecreasesValue,
                maxValue: balanceDecreasesMax,
            },
            {
                title: t('gameui.singleWinExceeds'),
                onChange: onWinExceedsValueChange,
                onSetEngineValue: setEngineWinExceedsValue,
                currentValue: winExceedsValue,
                maxValue: winExceedsMax,
            },
            {
                title: t('gameui.balanceIncreases'),
                onChange: onBalanceIncreasesValueChange,
                onSetEngineValue: setEngineBalanceIncreasesValue,
                currentValue: balanceIncreasesValue,
                maxValue: balanceIncreasesMax,
            },
        ],
        [
            balanceDecreasesValue,
            balanceDecreasesMax,
            onBalanceDecreasesValueChange,
            onWinExceedsValueChange,
            winExceedsValue,
            winExceedsMax,
            onBalanceIncreasesValueChange,
            balanceIncreasesValue,
            balanceIncreasesMax,
        ]
    )

    return items
}
